import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Divider,
    Alert,
    CircularProgress,
    Stack,
    Stepper,
    Step,
    StepLabel,
    Grid,
    LinearProgress,
    Chip,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Checkbox,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    AutoFixHigh as SyncIcon,
    Psychology as AIIcon,
    Info as InfoIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Pending as PendingIcon,
    Loop as ProcessingIcon,
    QuestionAnswer as QuestionAnswerIcon,
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { aiChatService } from '../services/aiChatService';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from '../components/ToastNotification';
import { FAQManagementTab } from './tabs/FAQManagementTab';

interface SyncProgress {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    total: number;
    details?: Array<{
        filename: string;
        progress: number;
        message: string;
        status: string;
    }>;
}

interface FailedTask {
    id: string;
    resource_id: string;
    ai_task_id: string;
    status: 'FAILED' | 'PARTIAL_SUCCESS';
    error: string;
    failed_chunks: number;
    successful_chunks: number;
    total_chunks: number;
    failed_chunk_indices: number[];
    retry_count: number;
    created_at: string;
    updated_at: string;
    title: string;
    filename: string;
    filepath: string;
}

export const AIKnowledgeManagementPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
    const [activeStep, setActiveStep] = useState(0);
    const [progress, setProgress] = useState<SyncProgress | null>(null);
    const [isPolling, setIsPolling] = useState(false);

    // Failed tasks state
    const [failedTasks, setFailedTasks] = useState<FailedTask[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [loadingFailed, setLoadingFailed] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
    const [failedPage, setFailedPage] = useState(0);
    const [failedRowsPerPage, setFailedRowsPerPage] = useState(10);

    const steps = [
        'Thu thập dữ liệu',
        'Đang xử lý tài liệu',
        'Hoàn tất tri thức'
    ];

    const fetchProgress = useCallback(async () => {
        try {
            const data = await aiChatService.getSyncProgress();
            setProgress(data);

            // Determine active step based on progress
            // Sync is complete when no pending or processing tasks remain
            const isComplete = data.total > 0 && data.pending === 0 && data.processing === 0;
            if (isComplete) {
                setActiveStep(3);
                setIsPolling(false);
            } else if (data.total > 0) {
                setActiveStep(1);
            }
        } catch (error) {
            console.error('Failed to fetch progress:', error);
            // Stop polling on network errors to prevent console spam
            setIsPolling(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchProgress();
    }, [fetchProgress]);

    // Polling effect
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isPolling) {
            interval = setInterval(fetchProgress, 3000); // Polling faster (3s) for details
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isPolling, fetchProgress]);

    const handleSync = async () => {
        try {
            setLoading(true);
            setActiveStep(0);

            const response = await aiChatService.syncKnowledge();

            if (response.success) {
                showSuccess('Tiến trình đồng bộ tri thức đã được bắt đầu!');
                setIsPolling(true);
                fetchProgress();
            } else {
                throw new Error(response.message);
            }
        } catch (error: any) {
            showError(error.message || 'Không thể đồng bộ tri thức AI');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = async () => {
        try {
            setLoading(true);
            const response = await aiChatService.resetKnowledge();
            if (response.success) {
                showSuccess('Đã xoá sạch dữ liệu tri thức. Bạn có thể bắt đầu đồng bộ lại.');
                setProgress(null);
                setActiveStep(0);
                setIsPolling(false);
            }
        } catch (error: any) {
            showError(error.message || 'Lỗi khi reset tri thức');
        } finally {
            setLoading(false);
        }
    };

    const completionPercentage = progress?.total ? Math.round((progress.completed / progress.total) * 100) : 0;

    const [tabIndex, setTabIndex] = useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
        // Load failed tasks when switching to tab 2
        if (newValue === 2) {
            loadFailedTasks();
        }
    };

    // Load failed tasks
    const loadFailedTasks = useCallback(async () => {
        try {
            setLoadingFailed(true);
            const data = await aiChatService.getFailedTasks();

            // Validate response is array
            if (Array.isArray(data)) {
                setFailedTasks(data);
            } else {
                console.error('Invalid response format:', data);
                setFailedTasks([]);
                showError('Dữ liệu trả về không hợp lệ');
            }
        } catch (error: any) {
            console.error('loadFailedTasks error:', error);
            setFailedTasks([]); // Set empty array to prevent crash
            showError(error.message || 'Không thể tải danh sách file lỗi');
        } finally {
            setLoadingFailed(false);
        }
    }, [showError]);

    // Retry single task
    const handleRetry = async (taskId: string) => {
        try {
            setRetrying(true);
            await aiChatService.retryTask(taskId);
            showSuccess('Đã gửi lại yêu cầu xử lý');
            loadFailedTasks(); // Refresh list
        } catch (error: any) {
            showError('Retry thất bại: ' + error.message);
        } finally {
            setRetrying(false);
        }
    };

    // Bulk retry
    const handleBulkRetry = async () => {
        try {
            setRetrying(true);
            const data = await aiChatService.retryTasksBulk(selectedIds);
            const successCount = data.results.filter((r: any) => r.success).length;
            showSuccess(`Đã retry ${successCount}/${selectedIds.length} file`);
            setSelectedIds([]);
            loadFailedTasks();
        } catch (error: any) {
            showError('Bulk retry thất bại');
        } finally {
            setRetrying(false);
        }
    };

    // Delete task
    const handleDeleteConfirm = async () => {
        if (!taskToDelete) return;
        try {
            await aiChatService.deleteFailedTask(taskToDelete);
            showSuccess('Đã xóa task');
            setDeleteConfirmOpen(false);
            setTaskToDelete(null);
            loadFailedTasks();
        } catch (error: any) {
            showError('Xóa thất bại: ' + error.message);
        }
    };

    // Bulk delete
    const handleBulkDelete = async () => {
        if (!window.confirm(`Xác nhận xóa ${selectedIds.length} task?`)) return;
        try {
            for (const taskId of selectedIds) {
                await aiChatService.deleteFailedTask(taskId);
            }
            showSuccess(`Đã xóa ${selectedIds.length} task`);
            setSelectedIds([]);
            loadFailedTasks();
        } catch (error: any) {
            showError('Bulk delete thất bại');
        }
    };

    // Select/deselect tasks
    const handleToggleSelect = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const selectedAll = failedTasks.length > 0 && selectedIds.length === failedTasks.length;
    const handleSelectAll = () => {
        setSelectedIds(selectedAll ? [] : failedTasks.map(t => t.id));
    };

    const formatDateTime = (dateStr: string) => {
        return new Date(dateStr).toLocaleString('vi-VN');
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <AIIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                    <Typography variant="h4" component="h1">
                        Quản Lý Tri Thức AI
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Cập nhật và tối ưu hóa bộ não của trợ lý ảo dựa trên dữ liệu thực tế của trường
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabIndex} onChange={handleTabChange}>
                    <Tab label="Đồng bộ & Trạng thái" icon={<SyncIcon />} iconPosition="start" />
                    <Tab label="Dạy Chatbot (FAQs)" icon={<QuestionAnswerIcon />} iconPosition="start" />
                    <Tab label="Lỗi xử lý" icon={<WarningIcon />} iconPosition="start" />
                </Tabs>
            </Box>

            {/* TAB 0: SYNC & STATUS */}
            <div role="tabpanel" hidden={tabIndex !== 0}>
                {tabIndex === 0 && (
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Card elevation={3}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SyncIcon color="primary" /> Đồng bộ hóa dữ liệu từ hệ thống
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />

                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        Tính năng này sẽ quét toàn bộ thông tin về Sách, Môn học, Danh mục và Tài liệu số
                                        để nạp vào bộ nhớ Chatbot. Quá trình này diễn ra hoàn toàn tự động trong nền.
                                    </Alert>

                                    <Box sx={{ py: 2, textAlign: 'center' }}>
                                        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                                            {steps.map((label) => (
                                                <Step key={label}>
                                                    <StepLabel>{label}</StepLabel>
                                                </Step>
                                            ))}
                                        </Stepper>

                                        {progress && progress.total > 0 && (
                                            <Box sx={{ mb: 4, px: 4 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Tiến độ tổng thể: {progress.completed}/{progress.total} tài liệu
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {completionPercentage}%
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={completionPercentage}
                                                    sx={{ height: 10, borderRadius: 5 }}
                                                />

                                                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                                                    <Chip
                                                        size="small"
                                                        icon={<PendingIcon />}
                                                        label={`Chờ: ${progress.pending}`}
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        size="small"
                                                        icon={<ProcessingIcon sx={{ animation: isPolling ? 'spin 2s linear infinite' : 'none' }} />}
                                                        label={`Đang xử lý: ${progress.processing}`}
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        size="small"
                                                        icon={<SuccessIcon />}
                                                        label={`Xong: ${progress.completed}`}
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                    {progress.failed > 0 && (
                                                        <Chip
                                                            size="small"
                                                            icon={<ErrorIcon />}
                                                            label={`Lỗi: ${progress.failed}`}
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>

                                                {/* Detailed Progress Section */}
                                                {progress.details && progress.details.length > 0 && (
                                                    <Box sx={{ mt: 3, textAlign: 'left', bgcolor: 'rgba(0,0,0,0.02)', p: 2, borderRadius: 2 }}>
                                                        <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                                            Chi tiết xử lý ({progress.details.length} tác vụ đang chạy):
                                                        </Typography>
                                                        <Stack spacing={2}>
                                                            {progress.details.map((item, idx) => (
                                                                <Box key={idx}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                        <Typography variant="caption" noWrap sx={{ maxWidth: '70%', fontWeight: 500 }}>
                                                                            {item.filename}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="primary">
                                                                            {Math.round(item.progress)}%
                                                                        </Typography>
                                                                    </Box>
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={item.progress}
                                                                        sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
                                                                    />
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                                                        {item.message || 'Đang kết nối AI Hub...'}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                                            onClick={handleSync}
                                            disabled={loading || isPolling}
                                            sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                                        >
                                            {loading ? 'Đang gửi yêu cầu...' : isPolling ? 'Đang trong tiến trình...' : 'Bắt đầu đồng bộ ngay'}
                                        </Button>

                                        {activeStep === 3 && progress.failed === 0 && (
                                            <Box sx={{ mt: 3, color: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                <SuccessIcon />
                                                <Typography variant="subtitle1" fontWeight="bold">Tất cả tri thức đã được cập nhật mới nhất!</Typography>
                                            </Box>
                                        )}

                                        {activeStep === 3 && progress.failed > 0 && (
                                            <Box sx={{ mt: 3, color: 'warning.main', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                <ErrorIcon color="warning" />
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    Hoàn tất ({progress.completed} thành công, {progress.failed} lỗi không tìm thấy file)
                                                </Typography>
                                            </Box>
                                        )}

                                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="medium"
                                                onClick={() => {
                                                    if (window.confirm('CẢNH BÁO: Hành động này sẽ xoá toàn bộ dữ liệu vector và task hiện tại. Bạn có chắc chắn muốn làm mới lại từ đầu không?')) {
                                                        handleReset();
                                                    }
                                                }}
                                                disabled={loading || isPolling}
                                            >
                                                Làm mới (Reset)
                                            </Button>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Chi tiết kỹ thuật
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />
                                    <Stack spacing={2}>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <InfoIcon color="action" />
                                            <Box>
                                                <Typography variant="subtitle2">AI Hub Chuyên Nghiệp</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Dữ liệu được xử lý qua hệ thống STT-Nova2 Professional API (stt.minhbv.com).
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <InfoIcon color="action" />
                                            <Box>
                                                <Typography variant="subtitle2">Vector hóa (RAG)</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Nội dung được chia nhỏ thành các đoạn (chunks) và chuyển đổi sang không gian vector 1536 chiều.
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <InfoIcon color="action" />
                                            <Box>
                                                <Typography variant="subtitle2">Tự động cập nhật</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Chatbot sẽ nhận thức được dữ liệu mới ngay khi tiến trình xử lý đạt trạng thái hoàn tất.
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </div>

            {/* TAB 1: FAQ MANAGEMENT */}
            <div role="tabpanel" hidden={tabIndex !== 1}>
                {tabIndex === 1 && <FAQManagementTab />}
            </div>

            {/* TAB 2: FAILED TASKS */}
            <div role="tabpanel" hidden={tabIndex !== 2}>
                {tabIndex === 2 && (
                    <Card elevation={3}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WarningIcon color="warning" />
                                    Danh sách file lỗi xử lý ({failedTasks.length})
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={loadFailedTasks}
                                    disabled={loadingFailed}
                                >
                                    Tải lại
                                </Button>
                            </Box>

                            {selectedIds.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={retrying ? <CircularProgress size={16} /> : <RefreshIcon />}
                                        onClick={handleBulkRetry}
                                        disabled={retrying}
                                    >
                                        Retry ({selectedIds.length})
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleBulkDelete}
                                    >
                                        Xóa ({selectedIds.length})
                                    </Button>
                                </Box>
                            )}

                            {loadingFailed ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : failedTasks.length === 0 ? (
                                <Alert severity="success">Không có file nào bị lỗi</Alert>
                            ) : (
                                <>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedAll}
                                                        onChange={handleSelectAll}
                                                    />
                                                </TableCell>
                                                <TableCell>Tên file</TableCell>
                                                <TableCell>Trạng thái</TableCell>
                                                <TableCell>Chunks thành công</TableCell>
                                                <TableCell>Chi tiết lỗi</TableCell>
                                                <TableCell>Thời gian</TableCell>
                                                <TableCell align="right">Thao tác</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {failedTasks
                                                .slice(failedPage * failedRowsPerPage, failedPage * failedRowsPerPage + failedRowsPerPage)
                                                .map(task => (
                                                    <TableRow key={task.id}>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={selectedIds.includes(task.id)}
                                                                onChange={() => handleToggleSelect(task.id)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{task.title || task.filename}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={task.status === 'PARTIAL_SUCCESS' ? 'Một phần' : 'Thất bại'}
                                                                color={task.status === 'PARTIAL_SUCCESS' ? 'warning' : 'error'}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {task.total_chunks > 0
                                                                ? `${task.successful_chunks}/${task.total_chunks} chunks`
                                                                : 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Tooltip title={task.error}>
                                                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                                    {task.error}
                                                                </Typography>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="caption">
                                                                {formatDateTime(task.updated_at)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                onClick={() => handleRetry(task.id)}
                                                                disabled={retrying}
                                                                color="primary"
                                                            >
                                                                <RefreshIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                onClick={() => {
                                                                    setTaskToDelete(task.id);
                                                                    setDeleteConfirmOpen(true);
                                                                }}
                                                                color="error"
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        component="div"
                                        count={failedTasks.length}
                                        page={failedPage}
                                        onPageChange={(_, newPage) => setFailedPage(newPage)}
                                        rowsPerPage={failedRowsPerPage}
                                        onRowsPerPageChange={(e) => {
                                            setFailedRowsPerPage(parseInt(e.target.value, 10));
                                            setFailedPage(0);
                                        }}
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        labelRowsPerPage="Số dòng:"
                                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                                    />
                                </>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Xác nhận xóa</DialogTitle>
                <DialogContent>
                    <Typography>Bạn có chắc chắn muốn xóa task này không?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Hủy</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Xóa
                    </Button>
                </DialogActions>
            </Dialog>

            <style>
                {`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                `}
            </style>

            <ToastNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={hideSnackbar}
            />
        </Box>
    );
};

export default AIKnowledgeManagementPage;
